import { Injectable } from '@angular/core';
import { UserMenuObj } from '@atpco/hub-ui';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { AuthRedirectInitiatedError } from '../auth/auth-redirect-initiated-error';
import { AuthService } from '../auth/auth.service';

type ProfileType = {
  givenName?: string;
  surname?: string;
  userPrincipalName?: string;
  id?: string;
};

@Injectable({
  providedIn: 'root',
})
export class DataService {
  user: UserMenuObj | ProfileType = {
    name: '',
    org: '',
    email: '',
    username: '',
    avatar: {
      src: 'user image goes here',
      alt: 'profile image',
    },
  };

  userSubject: BehaviorSubject<any> = new BehaviorSubject(this.user);

  constructor(private authService: AuthService, private router: Router) {}

  getUserAsObservable() {
    return this.userSubject.asObservable();
  }

  changeUser(value: any) {
    this.userSubject.next(value);
  }

  /**
   * Gets an API access token via AuthService.acquireApiTokenOrReauth (which
   * applies the shared retry/re-auth policy). Returns null when no token is
   * available — including when a re-auth redirect has been kicked off, in which
   * case the browser is navigating away and this return is moot.
   * @returns Promise<string | null>
   */
  async getAPIAccessTokenSilent(): Promise<string | null> {
    try {
      const result = await firstValueFrom(this.authService.acquireApiTokenOrReauth());
      return result.accessToken;
    } catch (error) {
      // Any required re-auth was already triggered centrally; there is nothing
      // usable to return either way.
      if (!(error instanceof AuthRedirectInitiatedError)) {
        console.error('Token acquisition failed', error);
      }
      return null;
    }
  }

  /**
   * This method gets the access token silently from the msal cache 
   * and decodes it to get the user role
   * @returns Promise<string | null>
   */
  async getUserRole(): Promise<string[] | null> {
    const token = await this.getAPIAccessTokenSilent();
    if (!token) {
      // No usable token is auth-required (a redirect is already in flight), not
      // an empty role set — return null so callers don't treat it as a denial.
      return null;
    }
    const decodedToken: any = jwtDecode(token);
    return decodedToken.roles ?? [];
  }

  /**
   * Check if user has role
   * @param role string 
   * @returns Promise<boolean> 
   */
  async userHasRole(role: string): Promise<boolean> {
    const userRoles = await this.getUserRole();
    if (userRoles && userRoles.length) {
      return userRoles.includes(role);
    } else {
      return false;
    }
  }

  /**
   * Check if user has role and redirect to access denied page if not
   * @param role string 
   * @returns Promise<boolean> 
   */
  async userHasRoleAndRedirect(role: string): Promise<boolean> {
    const userRoles = await this.getUserRole();
    if (userRoles === null) {
      // Auth-required, not a denial — the redirect is already in flight.
      return false;
    }
    return userRoles.includes(role) ? true : this.router.navigateByUrl('/access-denied');
  }
}